import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './PermitForm.css';
import { API_BASE } from '../config/apiConfig';

const IndyResidentialDevelopmentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    permitType: 'indy_res_dev',
    // Applicant / Contact
    firstName: '',
    lastName: '',
    email: '',
    phone: '',

    // Project Location
    streetAddress: '',
    addressLine2: '',
    city: 'Indianapolis',
    state: 'IN',
    zipCode: '',
    legalDescription: '',

    // Project Details
    projectType: '',
    description: '',
    squareFootage: '',
    projectValue: '',

    // Drainage / ILP
    subdivisionName: '',
    lotNumber: '',
    subdivisionPreApprovedForDrainage: '',
    additionOver400SqFt: '',
    hasDrainagePermit: '',
    hasImprovementLocationPermit: '',
    improvementLocationPermitNumber: '',

    // Notes
    notes: ''
  });

  // Auto-save draft (separate from West Lafayette building draft)
  useEffect(() => {
    const saveDraft = () => {
      const draftData = {
        formData,
        attachments: attachments.map(file => ({ name: file.name })),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('indyResDevDraft', JSON.stringify(draftData));
    };

    const timeoutId = setTimeout(saveDraft, 500);
    return () => clearTimeout(timeoutId);
  }, [formData, attachments]);

  useEffect(() => {
    if (!location.state?.prefillData) {
      const savedDraft = localStorage.getItem('indyResDevDraft');
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          const draftDate = new Date(draft.timestamp);
          const daysSinceDraft = (new Date() - draftDate) / (1000 * 60 * 60 * 24);
          if (daysSinceDraft < 7) {
            setFormData(draft.formData);
          } else {
            localStorage.removeItem('indyResDevDraft');
          }
        } catch (err) {
          console.error('Failed to load Indy res dev draft:', err);
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        ...location.state.prefillData,
        permitType: 'indy_res_dev'
      }));
      if (location.state.rejectionReason) {
        alert(`Previous application was rejected: ${location.state.rejectionReason}\n\nPlease review and correct your information before resubmitting.`);
      }
      localStorage.removeItem('indyResDevDraft');
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const multipartData = new FormData();

      // Add jurisdiction info from selected location
      const selectedLocationRaw = localStorage.getItem('selectedCounty');
      let submissionFields = { ...formData };
      if (selectedLocationRaw) {
        try {
          const loc = JSON.parse(selectedLocationRaw);
          submissionFields = {
            ...submissionFields,
            jurisdictionCity: loc.city || '',
            jurisdictionCounty: loc.county || '',
            jurisdictionState: loc.state || '',
            jurisdictionStateAbbr: loc.stateAbbr || ''
          };
        } catch (err) {
          console.error('Failed to parse selectedCounty for Indy residential dev:', err);
        }
      }

      // Append text fields
      Object.entries(submissionFields).forEach(([key, value]) => {
        multipartData.append(key, value ?? '');
      });

      // Append application/supporting docs as generic attachments
      attachments.forEach(file => {
        multipartData.append('attachments', file);
      });

      const response = await axios.post(`${API_BASE}/submit`, multipartData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        localStorage.removeItem('indyResDevDraft');
        navigate(`/status/${response.data.submissionId}`);
      }
    } catch (error) {
      alert('Failed to submit application: ' + (error.response?.data?.error || error.message));
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-card">
        <h2>Indianapolis – Residential Development Permit (Class 2)</h2>
        {location.state?.originalSubmissionId && (
          <div className="prefill-notice">
            <strong>ℹ️ Pre-filled from previous application</strong>
            <p>Original Submission ID: {location.state.originalSubmissionId}</p>
            <p>Please review and correct the information before submitting.</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* PROJECT LOCATION */}
          <div className="form-section">
            <h3>Project Location</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Address of Construction Site *</label>
                <input
                  type="text"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Address Line 2</label>
                <input
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>ZIP Code *</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Legal Description of Property</label>
                <textarea
                  name="legalDescription"
                  value={formData.legalDescription}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* APPLICANT / CONTACT */}
          <div className="form-section">
            <h3>Applicant / Contact Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* PROJECT DETAILS */}
          <div className="form-section">
            <h3>Project Details</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Project Type (e.g. New home, Addition, Remodel, Accessory Structure) *</label>
                <input
                  type="text"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Description of Work *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </div>
              <div className="form-group">
                <label>Estimated Square Footage</label>
                <input
                  type="number"
                  name="squareFootage"
                  value={formData.squareFootage}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Estimated Project Value ($)</label>
                <input
                  type="number"
                  name="projectValue"
                  value={formData.projectValue}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* DRAINAGE / ILP CONTEXT */}
          <div className="form-section">
            <h3>Drainage / Improvement Location Context</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Subdivision Name</label>
                <input
                  type="text"
                  name="subdivisionName"
                  value={formData.subdivisionName}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Lot Number</label>
                <input
                  type="text"
                  name="lotNumber"
                  value={formData.lotNumber}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Subdivision Pre-approved for Drainage?</label>
                <select
                  name="subdivisionPreApprovedForDrainage"
                  value={formData.subdivisionPreApprovedForDrainage}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <div className="form-group">
                <label>Addition Over 400 sq ft?</label>
                <select
                  name="additionOver400SqFt"
                  value={formData.additionOver400SqFt}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div className="form-group">
                <label>Drainage / Stormwater Permit Obtained?</label>
                <select
                  name="hasDrainagePermit"
                  value={formData.hasDrainagePermit}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="in_progress">In Progress</option>
                </select>
              </div>
              <div className="form-group">
                <label>Improvement Location Permit (ILP) Obtained?</label>
                <select
                  name="hasImprovementLocationPermit"
                  value={formData.hasImprovementLocationPermit}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="in_progress">In Progress</option>
                </select>
              </div>
              <div className="form-group">
                <label>ILP Permit Number (if applicable)</label>
                <input
                  type="text"
                  name="improvementLocationPermitNumber"
                  value={formData.improvementLocationPermitNumber}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* DOCUMENT UPLOADS */}
          <div className="form-section">
            <h3>What to Submit</h3>
            <p className="form-note">
              For this demo, please upload the completed Indianapolis Class 2 Residential Development/Structural Permit
              application PDF and any supporting plans (site plan, foundation plan, floor plans, wall section, elevations, truss specs).
            </p>
            <div className="form-group">
              <label>Application & Supporting Documents (PDFs) *</label>
              <input
                type="file"
                multiple
                accept=".pdf"
                onChange={handleFileChange}
                required={attachments.length === 0}
              />
              {attachments.length > 0 && (
                <p className="form-note-small">{attachments.length} file(s) selected</p>
              )}
            </div>
          </div>

          {/* NOTES */}
          <div className="form-section">
            <h3>Additional Notes</h3>
            <div className="form-group">
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                placeholder="Any extra context for reviewers"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IndyResidentialDevelopmentForm;

